import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import truncate from 'lodash/truncate';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ContentfulImage from 'hub/components/ContentfulImage';

function ArticleCard({
  title,
  description,
  hubslug,
  postCategory,
  heroImage,
  ...restProps
}) {
  const { t } = useTranslation('hub');

  return (
    <Link href={`/hub/${hubslug}`} passHref>
      <CardWrapper as="a" {...restProps}>
        <ImageWrapper>
          <ContentfulImage
            src={heroImage}
            layout="fill"
            objectFit="cover"
            alt={title}
          />
        </ImageWrapper>
        <ContentWrapper>
          {postCategory && (
            <CategoryLabel pb={1.5}>
              {t('hub:post.categoryLabel')}: {postCategory.title}
            </CategoryLabel>
          )}
          <Box as="header" mb={1}>
            <Typography
              variant="h5"
              component="h2"
              fontSIze="18px"
              color="textPrimary"
              className="article-title"
            >
              {title}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" fontSize="16px" mb={0}>
              {truncate(description, { length: 80 })}
            </Typography>
          </Box>
        </ContentWrapper>
      </CardWrapper>
    </Link>
  );
}

const CardWrapper = styled(Box)`
  text-decoration: none;
  color: currentColor;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)}px;
  height: 100%;
  width: 100%;
  min-height: 140px;
  position: relative;
  cursor: pointer;

  img {
    transition: transform 0.5s ease, filter 0.5s ease;
  }

  &:hover img {
    filter: brightness(0.9);
    transform: scale(1.1);
  }

  &:hover .article-title {
    text-decoration: underline;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(3)}px;
    height: 100%;
  }
`;

const CategoryLabel = styled(Typography)`
  font-size: 12px;
  font-weight: 400;
  color: #3c4340;
  line-height: 94%;
  letter-spacing: 0.4px;
`;

const ImageWrapper = styled.div`
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;
  min-height: 200px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 200px;
    width: 35%;
    min-height: 0;
  }
`;

const ContentWrapper = styled.div`
  align-self: center;
  width: 100%;

  ${(props) => props.theme.breakpoints.up('sm')} {
    width: 65%;
  }
`;

export default ArticleCard;
